import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import * as api from './api';
import type { CreateInvitationInput, InvitationListParams } from './types';

/** Query-key factory — every invitations key derives from here. */
export const invitationKeys = {
  all: ['invitations'] as const,
  lists: () => [...invitationKeys.all, 'list'] as const,
  list: (params: InvitationListParams) => [...invitationKeys.lists(), params] as const,
  inspect: (token: string) => [...invitationKeys.all, 'inspect', token] as const,
};

export function useInvitations(
  params: InvitationListParams = {},
  options: { enabled?: boolean } = {}
) {
  return useQuery({
    queryKey: invitationKeys.list(params),
    queryFn: () => api.getInvitations(params),
    placeholderData: keepPreviousData,
    enabled: options.enabled,
  });
}

export function useCreateInvitation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateInvitationInput) => api.createInvitation(input),
    onSuccess: () => qc.invalidateQueries({ queryKey: invitationKeys.lists() }),
  });
}

export function useResendInvitation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (publicId: string) => api.resendInvitation(publicId),
    onSuccess: () => qc.invalidateQueries({ queryKey: invitationKeys.lists() }),
  });
}

export function useRevokeInvitation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (publicId: string) => api.revokeInvitation(publicId),
    onSuccess: () => qc.invalidateQueries({ queryKey: invitationKeys.lists() }),
  });
}

/** Public token inspection (register page banner / invite landing). 404s and
 *  throttles must not retry — the state is rendered as invalid instead. */
export function useInvitationInspect(token: string) {
  return useQuery({
    queryKey: invitationKeys.inspect(token),
    queryFn: () => api.inspectInvitation(token),
    enabled: Boolean(token),
    retry: false,
    staleTime: Infinity,
  });
}

export function useAcceptInvitation() {
  return useMutation({
    mutationFn: (token: string) => api.acceptInvitation(token),
    // No invalidation: the caller refreshes the whole session (a brand-new
    // membership changes the bootstrap snapshot).
  });
}
